import { apiClient } from "./apiClient";
import { API_ENDPOINTS } from "../constants";
import type { ApiResponse } from "@/types";
import { buildListQuery, type ListParams, type Paginated } from "./agentService";
import type { AiPodGraph } from "../types/ai-pod-canvas";

export interface AiPodAgentTypeRef {
  id: string;
  aiPodId: string;
  agentTypeId: string;
  displayOrder: number;
  isRequired: boolean;
  agentType: {
    id: string;
    code: string;
    name: string;
    shortDescription?: string | null;
    isActive: boolean;
  };
}

/** What a subscription provisions: a chatbot, a workflow, or one of each. */
export type AiPodChannel = 'chat' | 'workflow' | 'both';

/** Settings a chat/both pod's chatbot is created with when a tenant subscribes. */
export type ChatbotTemplate = {
  name?: string;
  description?: string;
  greeting?: string;
  placeholder?: string;
  themeConfig?: Record<string, unknown>;
};

/** Marketing content shown on the tenant pod detail page (stored in pod metadata). */
export type AiPodContent = {
  modes?: string[];
  highlights?: Array<{ title: string; body: string }>;
  howItWorks?: Array<{ title: string; body: string }>;
  sidebarFeatures?: string[];
  pairWith?: string;
};

export interface AiPod {
  id: string;
  code: string;
  name: string;
  shortDescription?: string | null;
  description?: string | null;
  industryId?: string | null;
  categoryId?: string | null;
  industry?: { id: string; name: string } | null;
  category?: { id: string; name: string } | null;
  tenantId?: string | null;
  isActive: boolean;
  metadata: Record<string, unknown> & AiPodContent;
  channel: AiPodChannel;
  chatbotTemplate?: ChatbotTemplate | null;
  version: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  agentTypes: AiPodAgentTypeRef[];
  /** Whether the pod is set up enough to assign and sell; `missing` names what isn't. */
  readiness?: { ready: boolean; missing: string[] };
  [key: string]: any;
}

export type CreateAiPodDto = {
  code?: string;
  name: string;
  shortDescription?: string;
  description?: string;
  industryId?: string;
  categoryId?: string;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
  channel?: AiPodChannel;
  chatbotTemplate?: ChatbotTemplate;
  agentTypeIds: string[];
};

export type UpdateAiPodDto = Partial<CreateAiPodDto> & { version?: number };

export const aiPodService = {
  async listAiPods(params?: ListParams): Promise<Paginated<AiPod> & { aiPods: AiPod[] }> {
    const query = buildListQuery(params);
    const url = query ? `${API_ENDPOINTS.AI_PODS.LIST}?${query}` : API_ENDPOINTS.AI_PODS.LIST;
    const result = await apiClient.get<ApiResponse<any>>(url, { authenticate: true });
    return (result as any).data ?? (result as unknown as any);
  },

  async getAiPodById(id: string): Promise<AiPod> {
    const result = await apiClient.get<ApiResponse<AiPod>>(
      API_ENDPOINTS.AI_PODS.DETAILS(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as AiPod);
  },

  async createAiPod(payload: CreateAiPodDto): Promise<AiPod> {
    const result = await apiClient.post<ApiResponse<AiPod>>(
      API_ENDPOINTS.AI_PODS.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as AiPod);
  },

  async updateAiPod(id: string, payload: UpdateAiPodDto): Promise<AiPod> {
    const result = await apiClient.patch<ApiResponse<AiPod>>(
      API_ENDPOINTS.AI_PODS.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as AiPod);
  },

  async deleteAiPod(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.AI_PODS.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as { success: boolean });
  },

  async restoreAiPod(id: string): Promise<AiPod> {
    const result = await apiClient.post<ApiResponse<AiPod>>(
      API_ENDPOINTS.AI_PODS.RESTORE(encodeURIComponent(id)),
      {},
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as AiPod);
  },

  async getGraph(id: string): Promise<AiPodGraph> {
    const result = await apiClient.get<ApiResponse<AiPodGraph>>(
      API_ENDPOINTS.AI_PODS.GRAPH(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as AiPodGraph);
  },

  async saveGraph(id: string, graph: AiPodGraph): Promise<AiPodGraph> {
    const result = await apiClient.put<ApiResponse<AiPodGraph>>(
      API_ENDPOINTS.AI_PODS.GRAPH(encodeURIComponent(id)),
      graph,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as AiPodGraph);
  },
};
